import { computed, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, Subscription } from 'rxjs';
import { ISimulationGeneration } from 'src/app/interfaces/self-serve/isimulation-generation';
import { ISimulationGenerationStatusResponse } from 'src/app/interfaces/self-serve/responses/isimulation-generation-status-response';
import { SimulationGenerationStatus } from 'src/app/utils/enums/self-serve/simulation-generation-status.enum';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { SimulationGenerationLocalStorageUtil } from 'src/app/utils/simulation-generation-local-storage.util';
import { SseService } from 'src/app/services/sse.service';
import { AuthorService } from './author.service';

const POLL_INTERVAL_MS = 5_000;
const STALE_BACKOFF_INTERVAL_MS = 10_000;
const STALE_THRESHOLD_MS = 3 * 60_000;
const POLLING_CAP_MS = 20 * 60_000;
const WARNING_THRESHOLD_MS = 5 * 60_000;
// While an SSE stream is healthy, polls only act as a safety net — slow them
// down to avoid redundant traffic. Resumes at POLL_INTERVAL_MS on stream close.
const SSE_SAFETY_INTERVAL_MS = 60_000;
const SSE_CONTEXT_TYPE = 'simulation_generation';

// Drives terminalGenerations (the success/fail banner) — keep this limited to
// the true initial-generation outcomes. A refinement status must NOT be added
// here: the banner template only distinguishes Completed vs "everything else
// is Failed", so a refinement status would render as a false failure banner.
const TERMINAL_STATUSES = new Set<string>([
  SimulationGenerationStatus.Completed,
  SimulationGenerationStatus.Failed,
  SimulationGenerationStatus.TokenLimitExceeded,
]);

// Refinement runs on an already-generated simulation via a separate flow
// (CallRefinementPollingService); this service's own generation-polling loop
// has no business continuing once a call moves into refinement, so those
// statuses also stop the polling loop — just without feeding the banner.
const REFINEMENT_STATUSES = new Set<string>([
  SimulationGenerationStatus.RefinementRequested,
  SimulationGenerationStatus.RefinementChangePlan,
  SimulationGenerationStatus.RefinementSectionRewrite,
  SimulationGenerationStatus.RefinementCompleted,
  SimulationGenerationStatus.RefinementFailed,
]);

const STOP_POLLING_STATUSES = new Set<string>([
  ...TERMINAL_STATUSES,
  ...REFINEMENT_STATUSES,
]);

@Injectable({
  providedIn: 'root',
})
export class SimulationGenerationPollingService {
  private readonly tick = signal(0);
  private readonly activeTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // RxJS subscriptions for SSE event + closed$ per callUuid. Presence in this
  // map means SSE is active for that generation.
  private readonly sseSubscriptions = new Map<string, Subscription>();
  // Contexts for which SSE permanently failed (non-200, client cap, etc.).
  // Prevents endless re-connect attempts on subsequent safety polls.
  private readonly sseGivenUp = new Set<string>();
  // Signals that the next scheduled poll for a context should fire immediately
  // (0 ms) to close the snapshot→stream gap — a fresh SSE stream is
  // future-only, so a status change between the first poll response and when
  // the stream opened could be missed without this extra snapshot.
  private readonly sseGapPollNeeded = new Set<string>();

  readonly generations = computed(() => {
    this.tick();
    return SimulationGenerationLocalStorageUtil.getSimulationGenerations();
  });

  readonly terminalGenerations = computed(() =>
    this.generations().filter((g) => TERMINAL_STATUSES.has(g.status) && !g.dismissed),
  );

  constructor(
    private authorService: AuthorService,
    private sseService: SseService,
  ) {}

  /** Call once, at app bootstrap — starts one independent polling chain per still-unresolved record. */
  init(): void {
    this.generations()
      .filter((g) => !STOP_POLLING_STATUSES.has(g.status) && !g.pollingCapReached)
      .forEach((g) => this.startPollingFor(g.callUuid));
  }

  trackGeneration(
    callUuid: string,
    simulationName: string,
    moduleUuid?: string,
    simulationStatus?: string,
    details?: {
      scenario?: string;
      moduleName?: string;
      files?: { name: string; size: number; customerContentUuid: string }[];
      tagUuids?: string[];
    },
  ) {
    SimulationGenerationLocalStorageUtil.saveGeneration({
      callUuid,
      simulationName,
      status: SimulationGenerationStatus.Pending,
      startedAt: Date.now(),
      moduleUuid,
      simulationStatus,
      scenario: details?.scenario,
      moduleName: details?.moduleName,
      files: details?.files,
      tagUuids: details?.tagUuids,
    });
    this.bumpTick();
    this.startPollingFor(callUuid);
  }

  checkStatus(
    callUuid: string,
    fallback: { name: string; moduleUuid?: string; status?: string },
  ): Observable<ISimulationGeneration> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const wasCapped = !!this.getGeneration(callUuid)?.pollingCapReached;

    if (!this.getGeneration(callUuid)) {
      SimulationGenerationLocalStorageUtil.saveGeneration({
        callUuid,
        simulationName: fallback.name,
        moduleUuid: fallback.moduleUuid,
        status: fallback.status ?? SimulationGenerationStatus.Pending,
        startedAt: Date.now(),
      });
      this.bumpTick();
    } else if (wasCapped) {
      this.patchGeneration(callUuid, {
        pollingCapReached: false,
        startedAt: Date.now(),
        statusChangedAt: Date.now(),
      });
    }

    return this.authorService
      .getSimulationGenerationStatus(callUuid, accountUuid)
      .pipe(
        map((res: ISimulationGenerationStatusResponse) => {
          const updated = this.patchGeneration(callUuid, { status: res.status });
          if (!STOP_POLLING_STATUSES.has(res.status)) {
            this.startPollingFor(callUuid);
          }
          return updated;
        }),
        catchError(() => {
          return of(wasCapped
            ? this.patchGeneration(callUuid, { pollingCapReached: true })
            : this.getGeneration(callUuid)!);
        }),
      );
  }

  private patchGeneration(
    callUuid: string,
    patch: Partial<ISimulationGeneration>,
  ): ISimulationGeneration {
    const updated = { ...this.getGeneration(callUuid)!, ...patch };
    SimulationGenerationLocalStorageUtil.saveGeneration(updated);
    this.bumpTick();
    return updated;
  }

  isTerminalStatus(callUuid: string): boolean {
    return TERMINAL_STATUSES.has(this.getGeneration(callUuid)?.status ?? '');
  }

  isTakingLong(callUuid: string) {
    const generation = this.getGeneration(callUuid);
    return (
      !!generation &&
      !TERMINAL_STATUSES.has(generation.status) &&
      Date.now() - generation.startedAt >= WARNING_THRESHOLD_MS
    );
  }

  isPollingCapReached(callUuid: string): boolean {
    return !!this.getGeneration(callUuid)?.pollingCapReached;
  }

  dismiss(callUuid: string): void {
    const generation = this.getGeneration(callUuid);
    if (!generation) {
      return;
    }

    // Cancel any pending poll timer before cleaning up.
    const handle = this.activeTimers.get(callUuid);
    if (handle !== undefined) clearTimeout(handle);
    this.activeTimers.delete(callUuid);

    this.disconnectSse(callUuid);
    this.sseGivenUp.delete(callUuid);
    this.sseGapPollNeeded.delete(callUuid);

    if (generation.status === SimulationGenerationStatus.Completed) {
      const remaining = this.generations().filter((g) => g.callUuid !== callUuid);
      SimulationGenerationLocalStorageUtil.saveAllGenerations(remaining);
    } else {
      SimulationGenerationLocalStorageUtil.saveGeneration({ ...generation, dismissed: true });
    }
    this.bumpTick();
  }

  getGeneration(callUuid: string): ISimulationGeneration | undefined {
    return this.generations().find((g) => g.callUuid === callUuid);
  }

  private bumpTick() {
    this.tick.update((n) => n + 1);
  }

  private startPollingFor(callUuid: string): void {
    if (this.activeTimers.has(callUuid)) return; // already has an active chain — don't start a second one
    this.pollOne(callUuid);
  }

  private pollOne(callUuid: string): void {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    console.log(`[SSE] poll → callUuid=${callUuid}`);

    this.authorService
      .getSimulationGenerationStatus(callUuid, accountUuid)
      .subscribe({
        next: (res: ISimulationGenerationStatusResponse) => {
          const g = this.getGeneration(callUuid);
          if (!g) return; // record vanished (e.g. dismissed mid-flight) — stop

          console.log(
            `[SSE] poll response: callUuid=${callUuid} status=${res.status}` +
            (res.generationUuid ? ` generationUuid=${res.generationUuid}` : ' generationUuid=none (SR-21301 not deployed?)'),
          );

          // Open SSE before calling scheduleNextOrStop so the scheduler sees
          // the correct connected state and picks the right next interval.
          if (res.generationUuid) {
            if (STOP_POLLING_STATUSES.has(res.status)) {
              console.log(`[SSE] skipping SSE connect — status "${res.status}" is terminal/refinement`);
            } else if (this.sseSubscriptions.has(callUuid)) {
              console.log(`[SSE] skipping SSE connect — already subscribed for callUuid=${callUuid}`);
            } else if (this.sseGivenUp.has(callUuid)) {
              console.log(`[SSE] skipping SSE connect — previously gave up (cap or permanent failure) for callUuid=${callUuid}`);
            } else {
              this.connectSse(callUuid, res.generationUuid);
            }
          } else {
            console.log(`[SSE] no generationUuid in response — staying on ${POLL_INTERVAL_MS / 1000}s polling for callUuid=${callUuid}`);
          }

          // Do not let an older poll roll back a newer terminal SSE update.
          if (
            STOP_POLLING_STATUSES.has(g.status) &&
            !STOP_POLLING_STATUSES.has(res.status)
          ) {
            return;
          }

          const statusChanged = res.status !== g.status;
          SimulationGenerationLocalStorageUtil.saveGeneration({
            ...g,
            status: res.status,
            ...(res.generationUuid ? { generationUuid: res.generationUuid } : {}),
            statusChangedAt: statusChanged
              ? Date.now()
              : (g.statusChangedAt ?? g.startedAt),
          });
          this.bumpTick();
          this.scheduleNextOrStop(callUuid, res.status);
        },
        error: () => {
          console.warn(`[SSE] poll error for callUuid=${callUuid} — retrying on next interval`);
          // retry this one on the next interval, don't touch its stored status
          this.bumpTick();
          this.scheduleNextOrStop(
            callUuid,
            this.getGeneration(callUuid)?.status,
          );
        },
      });
  }

  /** Shared update path for SSE events — applies status + optional reason
   * through the same state mutation and scheduling as a poll response. */
  private applyStatusUpdate(callUuid: string, status: string, reason?: string): void {
    const g = this.getGeneration(callUuid);
    if (!g) return;

    // If we are past the polling cap, replayed SSE events (e.g. from an
    // EventSource reconnect after a laptop wake-up) must not overwrite the
    // stored status with a stale intermediate value and cannot be trusted to
    // deliver the final status in order. Disconnect the stream and fire one
    // fresh poll instead — the same recovery path plain polling uses.
    if (Date.now() - g.startedAt >= POLLING_CAP_MS) {
      console.log(`[SSE] past cap — ignoring replayed event "${status}" for callUuid=${callUuid}, falling back to poll`);
      this.disconnectSse(callUuid);
      this.pollOne(callUuid);
      return;
    }

    console.log(
      `[SSE] event → callUuid=${callUuid} status=${status}` +
      (reason ? ` reason=${reason}` : ''),
    );

    const statusChanged = status !== g.status;
    SimulationGenerationLocalStorageUtil.saveGeneration({
      ...g,
      status,
      ...(reason !== undefined ? { reason } : {}),
      statusChangedAt: statusChanged ? Date.now() : (g.statusChangedAt ?? g.startedAt),
    });
    this.bumpTick();
    this.scheduleNextOrStop(callUuid, status);
  }

  private connectSse(callUuid: string, generationUuid: string): void {
    console.log(`[SSE] connecting — callUuid=${callUuid} generationUuid=${generationUuid}`);
    this.sseService.connect(SSE_CONTEXT_TYPE, generationUuid);

    if (!this.sseService.isConnected(SSE_CONTEXT_TYPE, generationUuid)) {
      // Client cap reached — stay on normal polling intervals for this context.
      console.warn(`[SSE] cap reached — callUuid=${callUuid} will stay on ${POLL_INTERVAL_MS / 1000}s polling`);
      this.sseGivenUp.add(callUuid);
      return;
    }

    console.log(`[SSE] connected — subscribing to generation_status_changed for callUuid=${callUuid}`);

    const eventSub = this.sseService
      .on<{ status: string; reason?: string }>(SSE_CONTEXT_TYPE, generationUuid, 'generation_status_changed')
      .subscribe(({ status, reason }) => this.applyStatusUpdate(callUuid, status, reason));

    const closedSub = this.sseService.closed$(SSE_CONTEXT_TYPE, generationUuid).subscribe(() => {
      // Stream permanently died — unsubscribe, mark as given up, and resume
      // fast (5 s) polling immediately by cancelling any pending safety timer.
      console.warn(`[SSE] stream closed — callUuid=${callUuid} resuming ${POLL_INTERVAL_MS / 1000}s polling immediately`);
      const sub = this.sseSubscriptions.get(callUuid);
      if (sub) {
        sub.unsubscribe(); // safe to call from within this callback
        this.sseSubscriptions.delete(callUuid);
      }
      this.sseGivenUp.add(callUuid);
      this.sseGapPollNeeded.delete(callUuid);
      const handle = this.activeTimers.get(callUuid);
      if (handle !== undefined) clearTimeout(handle);
      this.activeTimers.delete(callUuid);
      this.startPollingFor(callUuid);
    });

    const sub = new Subscription();
    sub.add(eventSub);
    sub.add(closedSub);
    this.sseSubscriptions.set(callUuid, sub);

    // Signal that the very next scheduled poll should fire immediately (0 ms)
    // to close the snapshot → stream gap.
    this.sseGapPollNeeded.add(callUuid);
    console.log(`[SSE] gap poll queued — next poll fires immediately, then every ${SSE_SAFETY_INTERVAL_MS / 1000}s`);
  }

  private disconnectSse(callUuid: string): void {
    const sub = this.sseSubscriptions.get(callUuid);
    if (sub) {
      sub.unsubscribe();
      this.sseSubscriptions.delete(callUuid);
      console.log(`[SSE] unsubscribed — callUuid=${callUuid}`);
    }
    const generationUuid = this.getGeneration(callUuid)?.generationUuid;
    if (generationUuid) {
      this.sseService.disconnect(SSE_CONTEXT_TYPE, generationUuid);
    }
  }

  private scheduleNextOrStop(
    callUuid: string,
    latestStatus: string | undefined,
  ): void {
    const g = this.getGeneration(callUuid);
    if (!latestStatus || !g || STOP_POLLING_STATUSES.has(latestStatus)) {
      // Cancel any pending timer (e.g. a 60 s safety timer when SSE delivers
      // a terminal status) so it doesn't fire a redundant poll after we stop.
      const handle = this.activeTimers.get(callUuid);
      if (handle !== undefined) clearTimeout(handle);
      this.activeTimers.delete(callUuid);
      this.disconnectSse(callUuid);
      console.log(`[SSE] stopped — callUuid=${callUuid} reached terminal/refinement status "${latestStatus}"`);
      return;
    }

    if (Date.now() - g.startedAt >= POLLING_CAP_MS) {
      console.log(`[SSE] polling cap reached — callUuid=${callUuid} last status="${latestStatus}"`);
      const handle = this.activeTimers.get(callUuid);
      if (handle !== undefined) clearTimeout(handle);
      this.activeTimers.delete(callUuid);
      this.disconnectSse(callUuid);
      SimulationGenerationLocalStorageUtil.saveGeneration({
        ...g,
        pollingCapReached: true,
      });
      this.bumpTick();
      return;
    }

    const generationUuid = g.generationUuid;
    const sseConnected = !!generationUuid && this.sseService.isConnected(SSE_CONTEXT_TYPE, generationUuid);

    let nextInterval: number;
    let mode: string;
    if (sseConnected) {
      if (this.sseGapPollNeeded.has(callUuid)) {
        // One immediate follow-up to close the snapshot → stream gap.
        this.sseGapPollNeeded.delete(callUuid);
        nextInterval = 0;
        mode = 'SSE gap-poll (immediate)';
      } else {
        nextInterval = SSE_SAFETY_INTERVAL_MS;
        mode = `SSE safety (${SSE_SAFETY_INTERVAL_MS / 1000}s)`;
      }
    } else {
      const staleFor = Date.now() - (g.statusChangedAt ?? g.startedAt);
      const isStale = staleFor >= STALE_THRESHOLD_MS;
      nextInterval = isStale ? STALE_BACKOFF_INTERVAL_MS : POLL_INTERVAL_MS;
      mode = isStale
        ? `polling stale-backoff (${STALE_BACKOFF_INTERVAL_MS / 1000}s)`
        : `polling (${POLL_INTERVAL_MS / 1000}s)`;
    }

    console.log(`[SSE] next poll in ${nextInterval}ms — mode: ${mode} — callUuid=${callUuid}`);

    // Cancel any existing timer before setting a new one — prevents duplicate
    // chains if scheduleNextOrStop is called more than once for the same key
    // (e.g. a terminal SSE event arriving while a safety poll is still in flight).
    const existing = this.activeTimers.get(callUuid);
    if (existing !== undefined) clearTimeout(existing);

    const handle = setTimeout(() => this.pollOne(callUuid), nextInterval);
    this.activeTimers.set(callUuid, handle);
  }
}
